import type {
  AssemblyTokenClasses,
  AssemblySettings,
  As,
} from '../ModularTypes/AssemblySettings';
import type { ComponentPropsWithoutRef } from 'react';
import { assemblyTokenKeys } from '../ModularTypes/AssemblyTokenKey';

type ClassValue = string | undefined | null;

type AssemblyElementProps<T extends As> = Omit<
  ComponentPropsWithoutRef<T>,
  keyof AssemblyTokenClasses | 'as' | 'children'
>;

interface AssemblyBuildResult<T extends As> {
  className: string;
  elementProps: AssemblyElementProps<T>;
}

export class AssemblyClassBuilder {
  static build<T extends As>(
    props: AssemblySettings<T>,
    context: Partial<AssemblyTokenClasses> = {},
  ): AssemblyBuildResult<T> {
    return {
      className: this.buildClassName(props, context),
      elementProps: this.extractElementProps(props),
    };
  }

  private static buildClassName(
    props: AssemblySettings<As>,
    context: Partial<AssemblyTokenClasses> = {},
  ): string {
    const get = <T extends keyof AssemblyTokenClasses>(
      key: T,
    ): string | undefined => props[key] || context[key];

    const toClass = (prefix: string, value?: string) =>
      value ? `${prefix}-${value}` : null;

    return [
      'assembly',
      props.className ?? context.className,
      get('layout'),

      toClass('bg', get('background')),

      toClass('grid-template', get('gridTemplate')),

      toClass('positioning', get('positioning')),
      toClass('z-index', get('zIndex')),

      toClass('col-span', get('colSpan')),
      toClass('row-span', get('rowSpan')),

      toClass('gap', get('gap')),
      toClass('width', get('width')),
      toClass('width-min', get('widthMin')),
      toClass('width-max', get('widthMax')),
      toClass('height', get('height')),
      toClass('height-min', get('heightMin')),
      toClass('height-max', get('heightMax')),

      toClass('justify-content', get('justifyContent')),
      toClass('justify-items', get('justifyItems')),
      toClass('align-content', get('alignContent')),
      toClass('align-items', get('alignItems')),

      toClass('align-self', get('alignSelf')),
      toClass('justify-self', get('justifySelf')),

      toClass('padding-left', get('paddingLeft')),
      toClass('padding-right', get('paddingRight')),
      toClass('padding-top', get('paddingTop')),
      toClass('padding-bottom', get('paddingBottom')),
      toClass('margin', get('margin')),
      toClass('margin-left', get('marginLeft')),
      toClass('margin-right', get('marginRight')),
      toClass('margin-top', get('marginTop')),
      toClass('margin-bottom', get('marginBottom')),

      toClass('border-color', get('borderColor')),
      toClass('border-dark', get('borderDark')),
      toClass('border-light', get('borderLight')),
    ]
      .filter(this.isValid)
      .join(' ');
  }

  private static extractElementProps<T extends As>(
    props: AssemblySettings<T>,
  ): AssemblyElementProps<T> {
    const elementProps: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(props)) {
      if (
        key === 'as' ||
        key === 'children' ||
        assemblyTokenKeys.has(key as keyof AssemblyTokenClasses)
      ) {
        continue;
      }

      elementProps[key] = value;
    }

    return elementProps as AssemblyElementProps<T>;
  }

  static isValid(value: ClassValue): value is string {
    return typeof value === 'string' && value.trim() !== '';
  }
}
